import { BoxTruckMailingDto, BoxTruckMailingInput, htmlToSmsText } from '@numeo/types-main-web-app'
/**
 * Box Truck → Mailing. Operator-scheduled recurring broadcasts to their claimed drivers (the atrek
 * "mailing" scheduler): over Telegram, or over SMS from the company's own Quo line (ADR-043).
 *
 * LEFT: the mailing form (MailingForm) + the list of existing mailings (MailingList).
 * RIGHT: the operator's claimed drivers (MailingDriversTable) — only drivers REACHABLE on the chosen
 * service are selectable: a CONNECTED Telegram group, or a phone on file for SMS. Unconnected Telegram
 * rows offer a Connect button that opens the same Messaging Services panel the My Drivers detail uses,
 * and the row flips selectable in place once the group links.
 * Delivery itself runs server-side (search-scheduler tick → internal dispatch), at most once per day.
 * This page owns all state + API calls; the three components above are presentational.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { CalendarClock, Plus, Search as SearchIcon, X } from 'lucide-react'
import { Button, Modal, Spinner } from '@/components/ds2'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { useAuth } from 'hooks/auth'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { SnackbarType } from '@/utils/snackbar'
import { useDebouncedValue } from 'hooks/useDebouncedValue'
import { MessagingServicesSection } from 'pages/dashboard/pages/profiles/pages/drivers/components/driver-detail/MessagingServicesSection'
import { BroadcastProgressStrip } from '../components/BroadcastProgressStrip'
import { MailingDriversTable } from '../components/MailingDriversTable'
import { gapPresetLabel, isMailingReachable, isReachableOnlyOn } from '../utils/driverReachability'
import { collectRosterDriverIds } from '../utils/collectRosterDriverIds'
import { MailingForm } from '../components/MailingForm'
import { useSenderLines } from '../components/SenderLineSelect'
import { MailingList } from '../components/MailingList'
import { emptyMailingForm, MailingFormState } from '../utils/mailing'
import { bucketRecipients, trackBoxTruckMailingServiceChosen, trackBoxTruckUiInteraction } from '../utils/analytics'
import {
    BoxTruckVerification,
    createBoxTruckMailingApi,
    deleteBoxTruckMailingApi,
    fetchBoxTruckMailings,
    fetchMyBoxTruckDrivers,
    updateBoxTruckMailingApi,
} from '../services/boxTruckLoadsApi'

/** Drivers fetched per page in the mailing picker (mirrors My Drivers' infinite-scroll page size). */
const DRIVERS_PAGE_SIZE = 50

export const BoxTruckMailingPage = () => {
    const { showSnackbar } = useSnackbar()
    // The company's Quo workspace rides on the application the auth context already holds — no extra read,
    // and the form can say "connect it in Settings" without a permission the dispatcher may not have.
    const { application } = useAuth()
    const quo = application?.integrations?.quo?.[0] ?? null
    const [mailings, setMailings] = useState<BoxTruckMailingDto[]>([])
    const [drivers, setDrivers] = useState<BoxTruckVerification[]>([])
    // Server-side total of claimed drivers — when it exceeds the loaded rows the list is PARTIAL, which
    // surfaces a "showing X of N" hint and disables edit-time pruning (see startEdit).
    const [driversTotal, setDriversTotal] = useState(0)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [form, setForm] = useState<MailingFormState>(emptyMailingForm)
    // The driver whose Telegram-connect panel is open (unconnected row's Connect button).
    const [connectDriver, setConnectDriver] = useState<BoxTruckVerification | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<BoxTruckMailingDto | null>(null)
    // Driver picker: server-side paginated + searchable (like My Drivers) so the WHOLE fleet is reachable,
    // not just the first page.
    const [driversSearchInput, setDriversSearchInput] = useState('')
    const driversSearch = useDebouncedValue(driversSearchInput, 300)
    const [driversPage, setDriversPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    // The search string the currently-loaded `drivers`/`driversTotal` describe. Updated ATOMICALLY with them on
    // each successful load, so startEdit's prune gate never reads a live search against a stale (still-loading)
    // list — the window in which it would drop real recipients.
    const [loadedSearch, setLoadedSearch] = useState('')
    const [driversLoading, setDriversLoading] = useState(false)
    const [driversLoadingMore, setDriversLoadingMore] = useState(false)
    // Header "select all" spans the WHOLE connected fleet (all pages), not just the loaded page. The flag is
    // true only after an explicit full select, so a partially-loaded list never shows a misleading
    // fully-checked header (which could otherwise send a mailing to a partial fleet). `selectingAll` guards
    // the in-flight bulk fetch.
    const [allConnectedSelected, setAllConnectedSelected] = useState(false)
    const [selectingAll, setSelectingAll] = useState(false)
    // seqRef: only the LATEST driver load applies (a slow append can't land after a search reset). loadingRef:
    // synchronous latch so a scroll burst can't double-fire the same page.
    const seqRef = useRef(0)
    const loadingRef = useRef(false)

    // Fetch a page of the operator's claimed drivers. append=false replaces (fresh search / reload);
    // append=true adds the next page for infinite scroll. hasMore = the last RAW page came back full.
    const loadDrivers = useCallback(
        async (page: number, search: string, append: boolean) => {
            // Latch only APPENDS (mirrors My Drivers) — a fresh load / search reset / reload must never be
            // swallowed by an in-flight page-2 fetch, or the search box would read a term the list never applied.
            if (append && loadingRef.current) return
            loadingRef.current = true
            const seq = ++seqRef.current
            if (append) setDriversLoadingMore(true)
            else setDriversLoading(true)
            try {
                const { items, total } = await fetchMyBoxTruckDrivers(page, DRIVERS_PAGE_SIZE, search)
                if (seqRef.current !== seq) return // superseded by a newer load
                // Only the operator's OWN claimed drivers can receive a mailing (delivery = the claim record's
                // Telegram group), so an unclaimed row is dropped for display.
                const claimed = items.filter((d) => !!d.claimDriverId)
                setDrivers((prev) => (append ? [...prev, ...claimed] : claimed))
                setDriversTotal(total)
                setDriversPage(page)
                setLoadedSearch(search) // the search the loaded rows actually describe (for startEdit's prune gate)
                // Page by COUNT, not page-fullness: the server can return a short non-last page (it skips claims
                // with an invalid account id while `total` still counts them), which `items.length === PAGE` would
                // misread as "no more" and strand the rest of the fleet. `page * PAGE < total` stops exactly once.
                setHasMore(page * DRIVERS_PAGE_SIZE < total)
            } catch (e) {
                if (seqRef.current === seq) showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Failed to load drivers')
            } finally {
                if (seqRef.current === seq) {
                    setDriversLoading(false)
                    setDriversLoadingMore(false)
                }
                loadingRef.current = false
            }
        },
        [showSnackbar]
    )

    // Initial load: mailings + the first drivers page (one page spinner).
    useEffect(() => {
        let cancelled = false
        setLoading(true)
        void (async () => {
            try {
                const [mailingList] = await Promise.all([fetchBoxTruckMailings(), loadDrivers(1, '', false)])
                if (!cancelled) setMailings(mailingList)
            } catch (e) {
                if (!cancelled) showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Failed to load mailings')
            } finally {
                if (!cancelled) setLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [loadDrivers, showSnackbar])

    // Only once the mailing is actually on SMS — Telegram has no sender to choose.
    // `check` asks the server what became of the line this mailing stored — it may not be in the
    // caller's own options, and "outside my groups" and "Quo released it" send differently.
    /**
     * The line the OPENED mailing stored, which is the only line worth a verdict — and the reason this
     * is not `form.senderPhoneNumberId`: that follows the picker, so asking about it spent a request on
     * every selection, to ask about a line the operator had just chosen from the list and can see is
     * live. The question is "is the number this record names still in Quo", and it is settled when the
     * form opens.
     */
    const [openedLine, setOpenedLine] = useState<string | null>(null)
    const senderLines = useSenderLines(form.service === 'sms', openedLine)

    // Reload page 1 whenever the (debounced) search changes — filtering is server-side. Skip the initial
    // run: the mount effect already fetched page 1 with an empty search.
    const searchInitialized = useRef(false)
    useEffect(() => {
        if (!searchInitialized.current) {
            searchInitialized.current = true
            return
        }
        // The connected set changed under a new search — a prior full select no longer covers "all".
        setAllConnectedSelected(false)
        void loadDrivers(1, driversSearch, false)
    }, [driversSearch, loadDrivers])

    // Reset the form AND the select-all flag together — an empty form is never "all connected selected".
    // A new mailing names no line yet, so there is nothing to ask a verdict about.
    const resetForm = useCallback(() => {
        setForm(emptyMailingForm())
        setOpenedLine(null)
        setAllConnectedSelected(false)
    }, [])

    const loadMoreDrivers = useCallback(() => {
        if (driversLoading || driversLoadingMore || !hasMore) return
        void loadDrivers(driversPage + 1, driversSearch, true)
    }, [driversLoading, driversLoadingMore, hasMore, driversPage, driversSearch, loadDrivers])

    const patchForm = (patch: Partial<MailingFormState>) => {
        // Switching the service changes which drivers are reachable, so the selection starts over — a
        // Telegram-connected driver with no phone must not ride into an SMS mailing unseen.
        if (patch.service !== undefined && patch.service !== form.service) {
            setForm((f) => ({ ...f, ...patch, driverAccountIds: [] }))
            setAllConnectedSelected(false)
            return
        }
        setForm((f) => ({ ...f, ...patch }))
    }

    const toggleDriver = (userId: string) => {
        setForm((f) => ({
            ...f,
            driverAccountIds: f.driverAccountIds.includes(userId) ? f.driverAccountIds.filter((id) => id !== userId) : [...f.driverAccountIds, userId],
        }))
        // Any individual pick means the selection is no longer "the whole connected fleet".
        setAllConnectedSelected(false)
    }

    /**
     * Every driver in the fleet this search matches and this service can reach — all pages, not the
     * loaded ones.
     *
     * The walk itself is `collectRosterDriverIds`, shared with the SMS composer: both surfaces ask the
     * same question of the same endpoint, and two copies of "page one for the count, then the rest"
     * is how they end up disagreeing about who is selected.
     */
    const fetchAllConnectedIds = useCallback(
        async (search: string, service: MailingFormState['service'], only?: (d: BoxTruckVerification) => boolean): Promise<string[]> => {
            const { ids } = await collectRosterDriverIds(search, (d) => isMailingReachable(d, service) && (!only || only(d)))
            return ids
        },
        []
    )

    // Header select-all: select EVERY connected driver in the fleet (all pages) on check, clear on uncheck.
    // Not derived from the loaded rows, so it never claims "all" while only a page is selected.
    const toggleAllConnected = async () => {
        if (selectingAll) return // a bulk select is already in flight
        if (allConnectedSelected) {
            setForm((f) => ({ ...f, driverAccountIds: [] }))
            setAllConnectedSelected(false)
            return
        }
        setSelectingAll(true)
        try {
            const ids = await fetchAllConnectedIds(driversSearch, form.service)
            const searching = !!driversSearch.trim()
            // Under an active search "select all" means "add every MATCH" — MERGE with the existing selection so
            // recipients outside the current filter are never dropped (and a zero-match filter can't wipe the form).
            // With no search it IS the whole connected fleet, so a replace is exact.
            setForm((f) => ({ ...f, driverAccountIds: searching ? Array.from(new Set([...f.driverAccountIds, ...ids])) : ids }))
            // Only a no-search select covers the WHOLE connected fleet; a filtered select never does, so the
            // header must not render fully-checked (which would imply the whole fleet is selected).
            setAllConnectedSelected(!searching && ids.length > 0)
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t select all drivers')
        } finally {
            setSelectingAll(false)
        }
    }

    /**
     * Select every driver this service is the ONLY way to reach — on SMS, the ones with no Telegram group.
     *
     * Always a MERGE, never a replace: it is a subset of "select all", and an operator who ticked three
     * drivers by hand and then asked for the Telegram-less ones means both. The whole-fleet flag stays
     * off, because this never covers the whole fleet by construction.
     */
    const selectGap = async () => {
        if (selectingAll) return
        setSelectingAll(true)
        try {
            const ids = await fetchAllConnectedIds(driversSearch, form.service, (d) => isReachableOnlyOn(d, form.service))
            trackBoxTruckUiInteraction('mailing_select_preset', `${form.service}:gap:ok:${bucketRecipients(ids.length)}`)
            if (!ids.length) {
                showSnackbar(SnackbarType.INFO, `Every reachable driver is also ${form.service === 'sms' ? 'on Telegram' : 'textable'} — nothing to add.`)
                return
            }
            setForm((f) => ({ ...f, driverAccountIds: Array.from(new Set([...f.driverAccountIds, ...ids])) }))
            setAllConnectedSelected(false)
            showSnackbar(SnackbarType.SUCCESS, `${ids.length} driver${ids.length === 1 ? '' : 's'} ${gapPresetLabel(form.service)} added`)
        } catch (e) {
            // The walk is several reads; one failing is what makes the preset unusable on a big fleet,
            // and only the snackbar knew about it.
            trackBoxTruckUiInteraction('mailing_select_preset', `${form.service}:gap:failed`)
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn\u2019t select those drivers')
        } finally {
            setSelectingAll(false)
        }
    }

    const clearSelection = () => {
        setForm((f) => ({ ...f, driverAccountIds: [] }))
        setAllConnectedSelected(false)
    }

    const startEdit = (m: BoxTruckMailingDto) => {
        setForm({
            id: m._id,
            name: m.name,
            timeEST: m.timeEST,
            days: [...m.days],
            message: m.message,
            service: m.service,
            senderPhoneNumberId: m.senderPhoneNumberId ?? null,
            senderPhoneNumber: m.senderPhoneNumber ?? null,
            createdByMe: m.createdByMe,
            // Drop ids for drivers no longer in the list (released claims) — they can never receive a mailing
            // again, and keeping them makes the "N selected" counter disagree with the visible checkboxes.
            // ONLY when the loaded list is the WHOLE fleet: with a >page-size fleet, OR when a search filter is
            // active (so `drivers`/`driversTotal` describe the filtered subset, not the fleet), an absent id may
            // just be on a later page / filtered out — pruning it here would silently drop real recipients.
            // Gate on `loadedSearch` (what the loaded rows describe), NOT the live debounced `driversSearch`:
            // while a cleared search is refetching they diverge, and the live value would prune against a stale list.
            driverAccountIds:
                !loadedSearch.trim() && drivers.length >= driversTotal
                    ? m.driverAccountIds.filter((id) => drivers.some((d) => d.userId === id))
                    : [...m.driverAccountIds],
        })
        setOpenedLine(m.senderPhoneNumberId ?? null)
        // Editing loads a specific mailing's recipients — not an explicit "select all connected".
        setAllConnectedSelected(false)
    }

    const save = async () => {
        if (!form.name.trim()) return showSnackbar(SnackbarType.ERROR, 'Name is required')
        if (!form.days.length) return showSnackbar(SnackbarType.ERROR, 'Pick at least one day')
        if (!htmlToSmsText(form.message)) return showSnackbar(SnackbarType.ERROR, 'Message is required')
        if (form.message.length > 4000) return showSnackbar(SnackbarType.ERROR, 'Message is too long')
        if (!form.driverAccountIds.length) return showSnackbar(SnackbarType.ERROR, 'Select at least one reachable driver')
        setSaving(true)
        try {
            const input: BoxTruckMailingInput = {
                name: form.name,
                timeEST: form.timeEST,
                days: form.days,
                message: form.message,
                driverAccountIds: form.driverAccountIds,
                service: form.service,
                // Always sent, including as `null`: on a PATCH that is how the form says "back to my own
                // line", and leaving the key out would keep whatever the mailing already had.
                senderPhoneNumberId: form.service === 'sms' ? form.senderPhoneNumberId : null,
            }
            if (form.id) {
                const updated = await updateBoxTruckMailingApi(form.id, input)
                setMailings((prev) => prev.map((m) => (m._id === updated._id ? updated : m)))
                showSnackbar(SnackbarType.SUCCESS, 'Mailing updated')
            } else {
                const created = await createBoxTruckMailingApi(input)
                setMailings((prev) => [created, ...prev])
                showSnackbar(SnackbarType.SUCCESS, 'Mailing scheduled')
            }
            // Which service a mailing goes out on is the one choice that changes what it costs the
            // company and what the driver sees; the rest of the form is schedule and copy.
            trackBoxTruckMailingServiceChosen(form.service, form.service === 'sms' && !!form.senderPhoneNumberId)
            resetForm()
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t save the mailing')
        } finally {
            setSaving(false)
        }
    }

    const toggleEnabled = async (m: BoxTruckMailingDto) => {
        try {
            const updated = await updateBoxTruckMailingApi(m._id, { enabled: !m.enabled })
            setMailings((prev) => prev.map((x) => (x._id === updated._id ? updated : x)))
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t update the mailing')
        }
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        try {
            await deleteBoxTruckMailingApi(deleteTarget._id)
            setMailings((prev) => prev.filter((m) => m._id !== deleteTarget._id))
            if (form.id === deleteTarget._id) resetForm()
            setDeleteTarget(null)
        } catch (e) {
            showSnackbar(SnackbarType.ERROR, getUserErrorMessage(e) || 'Couldn’t delete the mailing')
        }
    }

    // Patch a driver's telegramGroup in place after a rename/disconnect from the modal.
    const onTelegramGroupChange = (userId: string) => (group: BoxTruckVerification['telegramGroup']) => {
        setDrivers((prev) => prev.map((d) => (d.userId === userId ? { ...d, telegramGroup: group } : d)))
        setConnectDriver((prev) => (prev && prev.userId === userId ? { ...prev, telegramGroup: group } : prev))
    }

    // A FRESH connect happens out-of-band (the operator pastes /connect into Telegram; the bot links the
    // group server-side), so the panel has no callback to fire — refetch when the modal closes so the row
    // actually flips selectable without a page reload.
    const closeConnectModal = () => {
        setConnectDriver(null)
        // A fresh connect linked the group server-side with no callback — reload the drivers (page 1, current
        // search) so the row flips selectable.
        void loadDrivers(1, driversSearch, false)
    }

    return (
        <div className="flex h-full flex-col bg-background text-foreground">
            <div className="flex items-center justify-between gap-3 border-b border-border px-6 py-3">
                <div>
                    <h1 className="flex items-center gap-2 text-base font-semibold">
                        <CalendarClock size={18} className="text-accent" /> Mailing
                    </h1>
                    <p className="text-xs text-foreground-muted">
                        Schedule recurring Telegram or SMS messages to your drivers (sent daily at the chosen EST time)
                    </p>
                </div>
                {form.id && (
                    <Button variant="outline" size="sm" prefix={<Plus size={14} />} onClick={() => resetForm()}>
                        New mailing
                    </Button>
                )}
            </div>

            {/* Today's runs. A mailing is queued at its EST minute and drained over the following few
                minutes, so this is the only place the operator can see that the whole fleet got it. */}
            <BroadcastProgressStrip watchIdle />

            {loading ? (
                <div className="flex flex-1 items-center justify-center py-24">
                    <Spinner color="accent" size={24} />
                </div>
            ) : (
                <div className="grid flex-1 grid-cols-1 gap-6 overflow-y-auto p-6 xl:grid-cols-[1.2fr_1fr]">
                    {/* LEFT — form + existing mailings */}
                    <div className="space-y-5">
                        <MailingForm
                            form={form}
                            saving={saving}
                            quo={quo}
                            senderLines={senderLines}
                            onPatch={patchForm}
                            onSave={() => void save()}
                            onCancel={() => resetForm()}
                        />
                        <MailingList mailings={mailings} onToggleEnabled={(m) => void toggleEnabled(m)} onEdit={startEdit} onDelete={setDeleteTarget} />
                    </div>

                    {/* RIGHT — driver picker */}
                    <div className="flex min-h-0 flex-col rounded-2xl bg-surface p-4 shadow-surface">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h2 className="text-sm font-semibold">
                                Drivers <span className="font-normal text-foreground-muted">({driversLoading ? '…' : driversTotal.toLocaleString()})</span>
                                {form.driverAccountIds.length > 0 && (
                                    <span className="ml-1.5 font-normal text-accent">· {form.driverAccountIds.length} selected</span>
                                )}
                                {selectingAll && <span className="ml-1.5 font-normal text-foreground-muted">· selecting all…</span>}
                            </h2>
                            {/* Search field — server-side, like My Drivers. */}
                            <div className="relative">
                                <SearchIcon size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-muted" />
                                <input
                                    type="text"
                                    value={driversSearchInput}
                                    onChange={(e) => setDriversSearchInput(e.target.value)}
                                    placeholder="Search name, phone, email…"
                                    className="h-8 w-60 rounded-lg border border-border bg-background pl-7 pr-2 text-xs text-foreground outline-none placeholder:text-foreground-muted/60 focus:border-accent"
                                />
                            </div>
                        </div>
                        <p className="mt-0.5 text-xs text-foreground-muted">
                            {form.service === 'sms'
                                ? 'Only drivers with a phone on their Box Truck profile can receive an SMS mailing.'
                                : 'Only drivers with a connected Telegram group can receive mailings — connect the rest from here.'}
                            {driversTotal > drivers.length ? ` Showing ${drivers.length} of ${driversTotal}.` : ''}
                        </p>
                        {/* Presets over the WHOLE fleet, not the loaded page — the header checkbox already walks
                            every page, and a shortcut that silently meant "the fifty on screen" would be worse
                            than none. The gap preset is the one an operator actually reaches for on SMS. */}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Button variant="outline" size="sm" loading={selectingAll} disabled={driversLoading} onClick={() => void selectGap()}>
                                Select all {gapPresetLabel(form.service)}
                            </Button>
                            {form.driverAccountIds.length > 0 && (
                                <Button variant="ghost" size="sm" disabled={selectingAll} onClick={clearSelection}>
                                    Clear selection
                                </Button>
                            )}
                            <span className="text-[11px] text-foreground-muted/70">
                                {form.service === 'sms'
                                    ? 'The drivers Telegram cannot reach — app instructions, a nudge to connect.'
                                    : 'The drivers with no number to text.'}
                            </span>
                        </div>
                        {/* Own scroll container so the list paginates independently (infinite scroll). */}
                        <div
                            className="mt-3 max-h-[calc(100vh-320px)] min-h-[200px] overflow-y-auto"
                            onScroll={(e) => {
                                const el = e.currentTarget
                                if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) loadMoreDrivers()
                            }}
                        >
                            {driversLoading ? (
                                <div className="flex justify-center py-10">
                                    <Spinner color="accent" size={20} />
                                </div>
                            ) : (
                                <MailingDriversTable
                                    drivers={drivers}
                                    service={form.service}
                                    selectedIds={form.driverAccountIds}
                                    onToggleDriver={toggleDriver}
                                    onToggleAll={() => void toggleAllConnected()}
                                    allSelected={allConnectedSelected}
                                    onConnect={setConnectDriver}
                                />
                            )}
                            {driversLoadingMore && (
                                <div className="flex justify-center py-3">
                                    <Spinner color="accent" size={16} />
                                </div>
                            )}
                            {!driversLoading && !driversLoadingMore && hasMore && (
                                <div className="flex justify-center py-3">
                                    <Button variant="ghost" size="sm" onClick={loadMoreDrivers}>
                                        Load more
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Telegram connect modal — the SAME Messaging Services panel the My Drivers detail renders. */}
            {connectDriver?.claimDriverId && (
                <Modal
                    open
                    onClose={closeConnectModal}
                    size="md"
                    header={
                        <div className="flex w-full items-center justify-between">
                            <h2 className="text-base font-semibold text-foreground">
                                Connect Telegram — {connectDriver.boxTruckInfo.info?.name || connectDriver.name || connectDriver.email}
                            </h2>
                            <button
                                type="button"
                                onClick={closeConnectModal}
                                className="rounded-md p-1 text-foreground-muted hover:bg-default hover:text-foreground"
                                aria-label="Close"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    }
                >
                    <MessagingServicesSection
                        driver={{ id: connectDriver.claimDriverId, telegramGroup: connectDriver.telegramGroup }}
                        onTelegramGroupChange={onTelegramGroupChange(connectDriver.userId)}
                    />
                </Modal>
            )}

            {deleteTarget && (
                <Modal
                    open
                    onClose={() => setDeleteTarget(null)}
                    size="sm"
                    header={<h2 className="text-base font-semibold text-foreground">Delete mailing</h2>}
                    footer={
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => void confirmDelete()}>
                                Delete
                            </Button>
                        </div>
                    }
                >
                    <p className="text-sm text-foreground">Delete “{deleteTarget.name}”? Scheduled sends stop immediately.</p>
                </Modal>
            )}
        </div>
    )
}
